import { ApiError, type ApiEnvelope } from "./types";

/**
 * Same-origin by default. In the multi-tenant deployment every tenant is reached at
 * `https://{slug}.os.blinkconnected.com`, and the edge routes `/api/*` on that host to that
 * tenant's API container and everything else to the web container. Calling `/api` relative
 * therefore always hits the right tenant with no per-tenant build, and there is no
 * cross-origin request (so no CORS preflight and no mixed-content trap).
 *
 * `NEXT_PUBLIC_API_BASE_URL` still overrides it for local development against a separately
 * hosted API (e.g. `http://localhost:5166/api`).
 */
export const DEFAULT_API_BASE_URL = "/api";

export function getApiBaseUrl(): string {
  const configured = process.env.NEXT_PUBLIC_API_BASE_URL?.trim();
  const base = configured && configured.length > 0 ? configured : DEFAULT_API_BASE_URL;
  return base.replace(/\/+$/, "");
}

/** Credentials the client stamps onto a request. */
export interface RequestAuthContext {
  accessToken?: string | null;
  companyId?: string | null;
  branchId?: string | null;
}

/**
 * Pulled fresh on every request so a token refreshed mid-flight is picked up by
 * the replay. Keeps this module free of React and of storage APIs.
 */
export type AuthContextSource = () => RequestAuthContext | null;

/**
 * Invoked at most once per request after a 401. Resolve `true` to have the
 * client replay the original request; the replay never triggers recovery again,
 * so this can never recurse.
 */
export type UnauthorizedHandler = (error: ApiError) => Promise<boolean>;

let authContextSource: AuthContextSource | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;
let inFlightRecovery: Promise<boolean> | null = null;

export function setAuthContextSource(source: AuthContextSource | null): void {
  authContextSource = source;
}

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
  inFlightRecovery = null;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Serialised as JSON. Use `undefined` for no body. */
  body?: unknown;
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Send no Authorization header at all (login, refresh). */
  anonymous?: boolean;
  /** Overrides the ambient auth context, e.g. to scope to another company. */
  auth?: RequestAuthContext;
  /** Disables the single 401 recovery attempt — required for the refresh call itself. */
  skipAuthRecovery?: boolean;
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const normalised = path.startsWith("/") ? path : `/${path}`;
  const url = `${getApiBaseUrl()}${normalised}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

function buildHeaders(options: RequestOptions): Headers {
  const headers = new Headers({ Accept: "application/json" });

  // FormData sets its own Content-Type, including the multipart boundary. Overriding it here
  // would produce a header the server cannot parse the body against.
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (!options.anonymous) {
    const context = options.auth ?? authContextSource?.() ?? null;
    if (context?.accessToken) {
      headers.set("Authorization", `Bearer ${context.accessToken}`);
    }
    if (context?.companyId) {
      headers.set("X-Company-Id", context.companyId);
    }
    if (context?.branchId) {
      headers.set("X-Branch-Id", context.branchId);
    }
  }

  for (const [key, value] of Object.entries(options.headers ?? {})) {
    headers.set(key, value);
  }

  return headers;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? (options.body === undefined ? "GET" : "POST"),
      headers: buildHeaders(options),
      body:
        options.body === undefined
          ? undefined
          : options.body instanceof FormData
            ? options.body
            : JSON.stringify(options.body),
      signal: options.signal,
      credentials: "omit",
      cache: "no-store",
    });
  } catch (cause) {
    // Aborts are a caller decision, not a failure — let them through untouched.
    if (cause instanceof Error && cause.name === "AbortError") throw cause;
    throw new ApiError({
      status: 0,
      errorCode: "network.unreachable",
      message: cause instanceof Error ? cause.message : "Network request failed",
      path,
    });
  }
}

/**
 * A success envelope may carry no body at all (204, or a command endpoint that
 * returns nothing), so an unparseable/empty body is not by itself an error.
 */
async function readEnvelope<T>(response: Response): Promise<Partial<ApiEnvelope<T>> | null> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as Partial<ApiEnvelope<T>>;
  } catch {
    return null;
  }
}

function fallbackMessage(response: Response): string {
  return response.statusText || `Request failed with status ${response.status}`;
}

function toApiError(
  response: Response,
  envelope: Partial<ApiEnvelope<unknown>> | null,
  path: string,
): ApiError {
  return new ApiError({
    status: response.status,
    message: envelope?.message ?? fallbackMessage(response),
    errorCode: envelope?.errorCode ?? null,
    errors: envelope?.errors ?? null,
    correlationId: envelope?.correlationId ?? null,
    path,
  });
}

async function unwrap<T>(response: Response, path: string): Promise<T> {
  const envelope = await readEnvelope<T>(response);
  if (!response.ok || envelope?.success === false) {
    throw toApiError(response, envelope, path);
  }
  // `undefined` is the correct value for endpoints declared as `Promise<void>`.
  return envelope?.data as T;
}

/** Collapses a burst of concurrent 401s into a single refresh attempt. */
function recoverFromUnauthorized(error: ApiError): Promise<boolean> {
  const handler = unauthorizedHandler;
  if (!handler) return Promise.resolve(false);

  if (!inFlightRecovery) {
    inFlightRecovery = handler(error)
      .catch(() => false)
      .finally(() => {
        inFlightRecovery = null;
      });
  }
  return inFlightRecovery;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  const canRecover =
    response.status === 401 && !options.anonymous && !options.skipAuthRecovery;
  if (!canRecover) {
    return unwrap<T>(response, path);
  }

  const error = toApiError(response, await readEnvelope(response), path);
  const recovered = await recoverFromUnauthorized(error);
  if (!recovered) throw error;

  // Exactly one replay, unwrapped directly — no second recovery pass is possible.
  return unwrap<T>(await send(path, options), path);
}

/** A file the API produced, already read into memory and named. */
export interface ApiFile {
  blob: Blob;
  fileName: string;
}

/** Pulls the download name out of `Content-Disposition`, falling back to the caller's. */
function fileNameOf(response: Response, fallback: string): string {
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) return decodeURIComponent(encoded[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : fallback;
}

/**
 * Fetches a binary file through the SAME authenticated path as every other request.
 *
 * Necessary because the API authenticates with a bearer token and nothing else: pointing a browser
 * tab straight at an export URL — `window.open(...)` — sends no Authorization header, and the
 * request is refused with 401 before a single byte is rendered. Reading the file here and handing
 * the caller a Blob is what lets it be downloaded, previewed or printed while still being fetched
 * as an authenticated request.
 */
export async function requestFile(
  path: string,
  fallbackFileName: string,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<ApiFile> {
  async function fetchOnce(): Promise<Response> {
    return send(path, { ...options, method: "GET", headers: { Accept: "*/*", ...options.headers } });
  }

  let response = await fetchOnce();

  // One replay after a refresh, exactly as `request` does, so an export never fails for the sole
  // reason that a token expired while the user was reading the screen.
  if (response.status === 401 && !options.anonymous && !options.skipAuthRecovery) {
    const error = toApiError(response, await readEnvelope(response), path);
    if (!(await recoverFromUnauthorized(error))) throw error;
    response = await fetchOnce();
  }

  if (!response.ok) {
    throw toApiError(response, await readEnvelope(response), path);
  }

  return { blob: await response.blob(), fileName: fileNameOf(response, fallbackFileName) };
}

export function apiGet<T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
  return request<T>(path, { ...options, method: "GET" });
}

export function apiPost<T>(
  path: string,
  body?: unknown,
  options: Omit<RequestOptions, "method" | "body"> = {},
): Promise<T> {
  return request<T>(path, { ...options, method: "POST", body: body ?? {} });
}
